import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import DataTable from "../ui/DataTable";
import Pagination from "../ui/Pagination";
import ConfirmDialog from "../ui/ConfirmDialog";
import { customizationService } from "../../services/customizationService";
import { projectService } from "../../services/projectService";
import toast from "react-hot-toast";

const MAX_NAME = 25;
const MAX_DESC = 100;

const TestCaseTypesTab = ({ clientId }) => {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [types, setTypes] = useState([]);
  const [currentPage, setCurrentPage] = useState(1)

  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [remapDialog, setRemapDialog] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [remapToId, setRemapToId] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);

  const loadProjects = useCallback(async () => {
    if (!clientId) return;
    try {
      const res = await projectService.getProjects({
        client_id: clientId,
        page: 1,
        limit: 500,
        is_active: true,
        is_deleted: false,
      });
      const list = res?.data?.projects || res?.projects || [];
      setProjects(list);
      if (list.length && !projectId) setProjectId(list[0].project_id);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load projects");
    }
  }, [clientId]);

  const loadTypes = useCallback(async () => {
    if (!clientId || !projectId) return;
    setLoading(true);
    try {
      const res = await customizationService.testCaseTypesList(projectId);
      setTypes(res?.data?.test_case_types || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load test case types");
      setTypes([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, projectId]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    loadTypes();
  }, [loadTypes]);

  const openCreate = () => {
    setEditId(null);
    setName("");
    setDescription("");
    setIsDefault(false);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditId(row.id);
    setName(row.name || "");
    setDescription(row.description || "");
    setIsDefault(!!row.is_default);
    setModalOpen(true);
  };

  const handleSave = async () => {
    const n = name.trim();
    if (!n) {
      toast.error("Name is required");
      return;
    }
    if (n.length > MAX_NAME) {
      toast.error(`Name must be at most ${MAX_NAME} characters`);
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await customizationService.testCaseTypesUpdate(projectId, editId, {
          name: n,
          description: description.trim().slice(0, MAX_DESC) || null,
          is_default: isDefault,
        });
        toast.success("Test case type updated");
      } else {
        await customizationService.testCaseTypesCreate(projectId, {
          name: n,
          description: description.trim().slice(0, MAX_DESC) || null,
          is_default: isDefault,
        });
        toast.success("Test case type created");
      }
      setModalOpen(false);
      loadTypes();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (row) => {
    setSelectedType(row);
    const others = types.filter((t) => t.id !== row.id && !t.is_default);
    if (others.length > 0) {
      setRemapToId(others[0].id);
      setRemapDialog(true);
    } else {
      setDeleteDialog(true);
    }
  };

  const confirmDelete = async () => {
    if (!selectedType) return;
    setDeleteLoading(true);
    try {
      await customizationService.testCaseTypesDelete(
        projectId,
        selectedType.id,
        remapDialog ? remapToId : undefined,
      );
      toast.success("Test case type deleted");
      setDeleteDialog(false);
      setRemapDialog(false);
      setSelectedType(null);
      loadTypes();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Delete failed");
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = [
    {
      key: "name",
      header: "Name",
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{row.name}</span>
          {row.is_default && (
            <Badge variant="outline" className="text-xs">
              Default
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: "Type",
      header: "System/Custom",
      render: (_, row) => <span className="font-medium">{row.is_system ? "System" : "Custom"}</span>
    },
    {
      key: "description",
      header: "Description",
      render: (_, row) => row.description || "—",
    },
    {
      key: "actions",
      header: "",
      render: (_, row) => (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => openEdit(row)}>
            <Pencil className="w-4 h-4" />
          </Button>
          {!row.is_default && (
            <Button
              variant="ghost"
              size="icon"
              className="text-red-600"
              onClick={() => openDelete(row)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];
  const data = useMemo(() => types?.slice((currentPage - 1) * 5, 5 * (currentPage)), [currentPage, types])

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-gray-600 text-sm mt-1">
            Manage test case types (e.g. Functional, Regression). Max 25 per
            project.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={projectId} onValueChange={(v) => setProjectId(v)}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Select project" />
            </SelectTrigger>
            <SelectContent>
              {projects.map((p) => (
                <SelectItem key={p.project_id} value={p.project_id}>
                  {p.name || p.project_id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={openCreate} disabled={!projectId}>
            <Plus className="w-4 h-4 mr-2" />
            Add New Case Type
          </Button>
        </div>
      </div>

      <div className="card overflow-hidden">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          emptyMessage={
            projectId
              ? "No test case types. Add one above."
              : "Select a project first."
          }
        />
        <Pagination
          currentPage={currentPage}
          totalPages={Math.ceil(types.length / 5)}
          totalItems={types.length}
          itemsPerPage={5}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              {editId ? "Edit Test Case Type" : "Add New Test Case Type"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">

            {/* Name */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-gray-700">Name *</label>
                <span className="text-xs text-gray-400">{name.length} / {MAX_NAME}</span>
              </div>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Automation Type"
                maxLength={MAX_NAME}
                disabled={!!editId && types.find((t) => t.id === editId)?.is_system}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white disabled:bg-gray-50 disabled:cursor-not-allowed"
              />
            </div>

            {/* Description */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-gray-700">Description *</label>
                <span className="text-xs text-gray-400">{description.length} / {MAX_DESC}</span>
              </div>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe your custom field"
                maxLength={MAX_DESC}
                rows={4}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white resize-y"
              />
            </div>

            {/* Default checkbox */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-gray-400 text-indigo-600 flex-shrink-0"
              />
              <div>
                <p className="text-sm font-bold text-gray-800">This case type is the default type.</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  The default case type is the pre-selected type for new cases
                  and the fallback type when you delete other case types.
                  Only one type can be selected as the default.
                </p>
              </div>
            </label>

          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        isOpen={deleteDialog}
        onClose={() => {
          setDeleteDialog(false);
          setSelectedType(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Test Case Type"
        message={`Are you sure you want to delete "${selectedType?.name}"?`}
        confirmText="Delete"
        confirmVariant="danger"
        loading={deleteLoading}
      />

      <Dialog open={remapDialog} onOpenChange={setRemapDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete &amp; Remap</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Test cases using &quot;{selectedType?.name}&quot; will be remapped
            to the selected type.
          </p>
          <div>
            <Label>Remap to</Label>
            <Select value={remapToId} onValueChange={setRemapToId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {types
                  .filter((t) => t.id !== selectedType?.id)
                  .map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRemapDialog(false);
                setSelectedType(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteLoading}
            >
              {deleteLoading ? "Deleting..." : "Delete & Remap"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TestCaseTypesTab;
